import { useLazyQuery, useMutation } from "@apollo/client";
import { useMediaQuery } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import { DeleteDialog } from "components/DeleteDialog";
import Dialog from "components/Dialog";
import { theme } from "config/theme";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { ProductProvider } from "contexts/ProductContext";
import { productReduce } from "data/actions/products";
import { ProductActyionType } from "data/types";
import { Product } from "generated/graphql";
import { useCurrentUser } from "hooks";
import React from "react";
import { uploadFile } from "utils";
import { CreateArticleDialog } from "../components/CreateArticleDialog";
import { LeftSideCatalog } from "../components/left";
import { RightSideCatalog } from "../components/right";
import { AddproductMutation, deleteProductMutation, updateProductMutation } from "../graphql/mutations";
import { allProductsQuery } from "../graphql/queries";

const useStyles = makeStyles(() =>
  createStyles({
   
    root: {
      backgroundColor: "#f5f6f8",
      display: "flex",
      flexDirection: "row",
      height: "100%",
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
      paddingTop: 30
    },
    root1: {
      backgroundColor: "#f5f6f8",
      display: "flex",
      width: "auto",
      flexDirection: "column",
      marginLeft: "auto",
      marginRight: "auto",
    },
    left: {
      width: theme.breakpoints.down("xs") ? "70%" : "67%",
      margin: "15px",
      [theme.breakpoints.down("xs")]: {
        margin: "0px",
        padding: "0px 5px",
        width: "100% !important",
      },
    },
    right: {
      width: "33%",
      backgroundColor: "#ffffff",
      padding: "20px",
    },
  })
);
export function CatalogList() {
  const styles = useStyles();
  const [selectedProduct, setSelectedProduct] = React.useState<Product>(null);
  const [open, setOpen] = React.useState(false);
  const [isUpdate, setIsUpdate] = React.useState(false);
  const onSelectItem = (product: Product) => setSelectedProduct(product);
  const handlerNewArticle = () => setOpen(true);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [createConfirm, setCreateConfirm] = React.useState(false);
  const [removeArticle] = useMutation(deleteProductMutation);
  const [addArticle] = useMutation(AddproductMutation);
  const [updateArticle] = useMutation(updateProductMutation);
  const [confirmUpdate, setConfirmUpdate] = React.useState(false);
  const [image, setImage] = React.useState(null);
  const [toUpdate, setToUpdate] = React.useState<Product>(null);
  const [toCreate, setToCreate] = React.useState<Product>(null);
  const [isLoading, setIsloading] = React.useState(false)
  const text = useText();
  const alertDispatch = useAlertDispatch();
  const { user } = useCurrentUser();
  const handlerNewArticleClose = () => {
    setIsUpdate(false);
    setOpen(false);
  };
  const [state, dispatch] = React.useReducer(productReduce, {
    products: [],
    totalPages: 0,
    countTotal: 0,
    currentPage: 0,
    limit: 10,
  });
  const [fetchProduct, { loading, refetch }] = useLazyQuery(allProductsQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      dispatch({
        type: ProductActyionType.FetchProduct,
        data: data.allProducts.data,
      });
    },
  });

  React.useEffect(() => {
    if (user?.company_user_clientId) {
      fetchProduct({
        variables: {
          queries: {
            limit: 10,
            page: 1,
            clientId: user?.company_user_clientId,
          },
        },
      });
    }
  }, [user]);

  const confirmCreation = (product: Product, image: any) => {
    setImage(image);
    if (isUpdate) {
      setToUpdate(product);
      setConfirmUpdate(true);
    } else {
      setCreateConfirm(true);
      setToCreate(product);
    }
  };

  const onDeleteHandled = () => {
    setOpenDeleteDialog(true);
  };

  const onUpdateHandled = () => {
    setOpen(true);
    setIsUpdate(true);
  };

  const handleConfirmDelete = () => {
    removeArticle({
      variables: {
        input: { code: selectedProduct.code },
      },
    })
      .then((response) => {
        const { message, statusCode } = response.data.deleteProduct;
        alertDispatch({
          message,
          severity: statusCode === "10000" ? "success" : "warning",
        });
        if (statusCode === "10000") {
          refetch();
        }
        setSelectedProduct(null);
        setOpenDeleteDialog(false);
      })
      .catch(() => {});
  };
  const updateArticleHandled = async () => {
    setConfirmUpdate(false);
    const url = await uploadFile(image);
    setImage(null);
    updateArticle({
      variables: {
        input: {
          code: toUpdate.code,
          product: {
            name: toUpdate.name,
            description: toUpdate.description,
            volume: Number(toUpdate.volume),
            value: Number(toUpdate.value),
            weight: toUpdate.weight,
            category: toUpdate.category.code,
            reference: toUpdate.reference ?? undefined,
            photoUrl: url ?? toUpdate.photoUrl,
          },
        },
      },
    })
      .then((response) => {
        const { statusCode, message, data } = response.data.updateProduct;
        alertDispatch({
          message,
          severity: statusCode === "10000" ? "success" : "warning",
        });
        if (statusCode === "10000") {
          setOpen(false);
          dispatch({
            data: { ...data },
            type: ProductActyionType.UpdateProduct,
          });
          setSelectedProduct(data);
          setIsUpdate(false);
        }
      })
      .catch(() => {});
  };

  const createArticle = async () => {
    setCreateConfirm(false);
    setIsloading(true)
    const url = await uploadFile(image);
    setImage(null);
    addArticle({
      variables: {
        input: {
          clientId: user.company_user_clientId,
          product: {
            ...toCreate,
            category: toCreate.category.code,
            volume: Number(toCreate.volume),
            value: Number(toCreate.value),
            photoUrl: url ?? undefined,
          },
        },
      },
    })
      .then((response) => {
        const { statusCode, message, data } = response.data.addProduct;
        alertDispatch({
          message,
          severity: statusCode === "10000" ? "success" : "warning",
        });
        if (statusCode === "10000") {
          setOpen(false);
          setCreateConfirm(false);
          dispatch({
            data: { ...data },
            type: ProductActyionType.CreateProduct,
          });
          setSelectedProduct(data);
          setIsloading(false)
          handlerNewArticleClose()
        } else {
          setSelectedProduct(null);
        }
      })
      .catch(() => {});
  };

  const handleCancelDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };
  const handleCloseDialogConfirmation = () => {
    setCreateConfirm(false);
  };
  const handleCloseUpdate = () => {
    setConfirmUpdate(false);
  };
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));


  return (
    <ProductProvider>
      <div className={styles.root}>
        <div className={isSmallScreen ? styles.root1 : styles.root}>
          <div className={styles.left}>
            <LeftSideCatalog
              onSelectItem={onSelectItem}
              handleNewArticle={handlerNewArticle}
              state={state}
              loading={loading}
              refetch={refetch}
            />
          </div>
          {!isSmallScreen && (
            <div className={styles.right}>
              <RightSideCatalog
                product={selectedProduct}
                onDeleteHandled={onDeleteHandled}         
                onUpdateHandled={onUpdateHandled}
              />
            </div>
          )}
        </div>
        <CreateArticleDialog
          open={open}
          isUpdate={isUpdate}
          productToUpdate={{ ...selectedProduct }}
          handlerNewArticleClose={handlerNewArticleClose}
          confirmCreation={confirmCreation}
          loading={isLoading}
        />
        <DeleteDialog
          open={openDeleteDialog}
          title={text.text.catalog.title_dialog_delete}
          handlerCancel={handleCancelDeleteDialog}
          handlerConfirm={handleConfirmDelete}
          message={`${text.text.all.sure_to_delete_article}`}
        />
        <Dialog
          open={createConfirm}
          title={text.text.all.confirmation}
          handleClose={handleCloseDialogConfirmation}
          handleConfirm={createArticle}
        >
          {`${text.text.all.are_done_creating} article ?`}
        </Dialog>
        <Dialog
          open={confirmUpdate}
          title={text.text.all.update}
          handleClose={handleCloseUpdate}
          handleConfirm={updateArticleHandled}
        >
          {`${text.text.all.are_done_updating} article ?`}
        </Dialog>
      </div>
    </ProductProvider>
  );
}

export default CatalogList;
